import posthog from 'posthog-js';
import { DEFAULT_PAYMENT_TERMS } from '../components/buyer_invoice_form/InvoicePaymentTermComponent';
import { TIME_IN } from '../components/supplier_invoice_form/LaborTimeSelect';
import { nullSafeGet, nullSafeGetOrElse, nullSafeGetWithZero } from './DataAccessUtils';
import { capitalize, DATE_ONLY_FORMAT, dateFormatter, roundToNearest } from './DataFormatterUtils';
import { adaptParsedInvoiceToFormData } from './InvoiceDataAdapter';

export const LINE_ITEM_FIELD = {
	MISC: 'misc',
	TRAVEL: 'travel',
	FREIGHT: 'freight',
	INCURRED: 'incurredCost',
};

export const LINE_ITEM_FIELD_TO_DISPLAY_NAMES = {
	[LINE_ITEM_FIELD.MISC]: 'Miscellaneous',
	[LINE_ITEM_FIELD.TRAVEL]: 'Travel',
	[LINE_ITEM_FIELD.FREIGHT]: 'Freight',
	[LINE_ITEM_FIELD.INCURRED]: 'Incurred Cost',
};

export const getFixedFloatStringOrZero = (value) =>
	value ? `${parseFloat(value).toFixed(2)}` : '0';

// Stringify a line-item value, preserving a genuine 0 (which a truthy check would
// otherwise drop). Only null/undefined/'' fall back to `fallback`. Note: no default
// on `fallback` — a default would rewrite an explicit `undefined` fallback to '',
// dropping the intended field-omission behavior at the simple/equipment call sites.
export const stringifyLineItemValue = (value, fallback) =>
	value !== undefined && value !== null && value !== '' ? `${value}` : fallback;

export const getFixedFloatSum = (value1, value2) =>
	(parseFloat(value1) + parseFloat(value2)).toFixed(2);

export const getFixedFloatDifference = (value1, value2) =>
	(parseFloat(value1 || '0') - parseFloat(value2 || '0')).toFixed(2);

export const recalculateInvoiceTotalFields = (form) => {
	const subtotals = form.getFieldsValue([
		'laborTotalBeforeTax',
		'partsTotalBeforeTax',
		'equipmentTotalBeforeTax',
		'freightTotalBeforeTax',
		'travelTotalBeforeTax',
		'miscTotalBeforeTax',
		'invoiceTax',
	]);
	const invoiceTotalBeforeTax = (
		parseFloat(nullSafeGetOrElse('laborTotalBeforeTax', subtotals, 0)) +
		parseFloat(nullSafeGetOrElse('partsTotalBeforeTax', subtotals, 0)) +
		parseFloat(nullSafeGetOrElse('equipmentTotalBeforeTax', subtotals, 0)) +
		parseFloat(nullSafeGetOrElse('freightTotalBeforeTax', subtotals, 0)) +
		parseFloat(nullSafeGetOrElse('travelTotalBeforeTax', subtotals, 0)) +
		parseFloat(nullSafeGetOrElse('miscTotalBeforeTax', subtotals, 0))
	).toFixed(2);

	const invoiceTotalAfterTax = (
		parseFloat(invoiceTotalBeforeTax) + parseFloat(nullSafeGetOrElse('invoiceTax', subtotals, 0))
	).toFixed(2);

	const materialTotalBeforeTax = (
		parseFloat(nullSafeGetOrElse('partsTotalBeforeTax', subtotals, 0)) +
		parseFloat(nullSafeGetOrElse('equipmentTotalBeforeTax', subtotals, 0))
	).toFixed(2);

	form.setFieldsValue({
		invoiceTotalBeforeTax,
		invoiceTotalAfterTax,
		materialTotalBeforeTax,
	});
};

const getSimpleLineItems = (values, fieldName) =>
	(values[`${fieldName}Keys`] || []).map((key) => {
		const amount = values[`${fieldName}-${key}-amount`];
		return {
			description: values[`${fieldName}-${key}-description`],
			id: values[`${fieldName}-${key}-id`],
			unitPrice: stringifyLineItemValue(values[`${fieldName}-${key}-unitPrice`], undefined),
			amount: `${amount}`,
		};
	});

export const getQuoteInvoiceLineItems = (values, laborTimeIn) => {
	const laborItems = (values.laborKeys || []).map((laborKey) => {
		let laborItem = {
			roleType: values[`labor-${laborKey}-roleType`],
			numberOfTech: parseInt(values[`labor-${laborKey}-numberOfTech`], 10),
			seconds: roundToNearest(
				parseFloat(values[`labor-${laborKey}-time`]) *
					60 *
					(laborTimeIn === TIME_IN.MINUTES ? 1 : 60),
				1
			),
			hourlyRate: values[`labor-${laborKey}-hourlyRate`],
			amount: values[`labor-${laborKey}-amount`],
		};
		return laborItem;
	});

	const partsItems = (values.partsKeys || []).map((partsKey, i) => {
		let partAmt = values[`parts-${partsKey}-amount`];
		let partItem = {
			description: values[`parts-${partsKey}-description`],
			partId: values[`parts-${partsKey}-partId`] || i + 1,
			partNumber: values[`parts-${partsKey}-partNumber`],
			unitType: values[`parts-${partsKey}-unitType`],
			quantity: stringifyLineItemValue(values[`parts-${partsKey}-quantity`], ''),
			unitPrice: stringifyLineItemValue(values[`parts-${partsKey}-unitPrice`], ''),
			amount: `${partAmt}`,
		};
		return partItem;
	});

	const equipmentItems = (values.equipmentKeys || []).map((equipmentKey, i) => {
		let equipmentAmt = values[`equipment-${equipmentKey}-amount`];
		let equipmentItem = {
			description: values[`equipment-${equipmentKey}-description`],
			equipmentId: values[`equipment-${equipmentKey}-equipmentId`] || i + 1,
			equipmentNumber: values[`equipment-${equipmentKey}-equipmentNumber`],
			quantity: '1',
			unitType: 'each',
			unitPrice: stringifyLineItemValue(values[`equipment-${equipmentKey}-unitPrice`], undefined),
			amount: `${equipmentAmt}`,
		};
		return equipmentItem;
	});

	const miscItems = getSimpleLineItems(values, LINE_ITEM_FIELD.MISC);
	const freightItems = getSimpleLineItems(values, LINE_ITEM_FIELD.FREIGHT);
	const travelItems = getSimpleLineItems(values, LINE_ITEM_FIELD.TRAVEL);
	const incurredItems = getSimpleLineItems(values, LINE_ITEM_FIELD.INCURRED);

	const materialLineItems = partsItems.concat(equipmentItems);

	return {
		laborItems,
		miscItems,
		freightItems,
		travelItems,
		incurredItems,
		materialLineItems,
	};
};

export const getInvoiceEntityCommonValues = (values, formData, scope, currentUser, laborTimeIn) => {
	const { laborItems, miscItems, materialLineItems, travelItems, freightItems } =
		getQuoteInvoiceLineItems(values, laborTimeIn);

	const taxLineItems = (values.taxKeys || []).map((taxKey) => {
		return {
			taxType: `${values[`tax-${taxKey}-taxType`]}`,
			amount: `${values[`tax-${taxKey}-amount`]}`,
		};
	});

	return {
		id: values.id,
		scope,
		externalWorkOrderId: values.externalWorkOrderId,
		invoiceNumber: values.invoiceNumber,
		paymentTerms: values.paymentTerms,
		dueDate: values.dueDate,
		dateOfInvoice: values.dateOfInvoice,
		invoiceTaxRate: values.invoiceTaxRate,
		invoiceTax: getFixedFloatStringOrZero(values.invoiceTax),
		invoiceTotalBeforeTax: getFixedFloatStringOrZero(values.invoiceTotalBeforeTax),
		invoiceTotalAfterTax: getFixedFloatStringOrZero(values.invoiceTotalAfterTax),
		laborTotalBeforeTax: getFixedFloatStringOrZero(values.laborTotalBeforeTax),
		laborLineItems: laborItems || [],
		taxLineItems: taxLineItems || [],
		materialTotalBeforeTax: getFixedFloatStringOrZero(values.materialTotalBeforeTax),
		materialLineItems: materialLineItems || [],
		freightTotalBeforeTax: getFixedFloatStringOrZero(values.freightTotalBeforeTax),
		freightLineItems: freightItems || [],
		travelTotalBeforeTax: getFixedFloatStringOrZero(values.travelTotalBeforeTax),
		travelLineItems: travelItems || [],
		miscTotalBeforeTax: getFixedFloatStringOrZero(values.miscTotalBeforeTax),
		miscLineItems: miscItems || [],
		workOrderId: parseInt(formData.workOrderId, 10),
		createdBy: currentUser.email,
		attachments: values.attachments,
		isAiParsed: formData.isAiParsed || false,
		isAiParsedEdited: formData.isAiParsedEdited || false,
		aiParseMismatchSignedByEmail: formData.aiParseMismatchSignedByEmail || null,
	};
};

// Shared helper to build an invoice payload from InvoiceFormBase-style values
export interface BuildInvoicePayloadOptions {
	// The invoice is anchored to exactly one of these. The API rejects a payload carrying
	// both keys or neither, so only the one that applies is written into the payload.
	workOrderId?: string | number;
	projectId?: string | number;
	currentUser: any;
	role: 'buyer' | 'supplier';
	baseMeta?: {
		buyerFacilityId?: any;
		supplierFacilityId?: any;
		invoicePDFLink?: string;
		isDeleted?: boolean;
		disputeNotes?: string;
	};
	// True when the payload updates an existing invoice rather than creating one. It
	// changes how a cleared optional field is expressed: an update must say "clear this"
	// with an explicit null, where a create simply omits the key.
	isUpdate?: boolean;
	// Project invoices only — there is no work order to read them off. buyerCompanyId is
	// required by the API; locationId is optional and simply omitted when unset (it keeps
	// the invoice in location-scoped spend reporting when it is set).
	locationId?: string | number;
	buyerCompanyId?: string | number;
	status?: string;
}

export const buildInvoicePayloadFromFormValues = (
	values: any,
	scope: string | null | undefined,
	options: BuildInvoicePayloadOptions
) => {
	const {
		workOrderId,
		projectId,
		currentUser,
		role,
		baseMeta,
		status,
		isUpdate,
		locationId,
		buyerCompanyId,
	} = options;
	const isProjectInvoice = projectId !== undefined && projectId !== null;

	const hasLineItems = (items: any) => items && Array.isArray(items) && items.length > 0;

	const laborLineItemsArray = values.laborLineItems || [];
	const laborLineItems = hasLineItems(laborLineItemsArray)
		? laborLineItemsArray.map((item: any) => ({
				roleType: item.roleType || '',
				numberOfTech: Number(item.numberOfTech || 0),
				seconds: Number(item.seconds || 0),
				hourlyRate: String(item.hourlyRate || 0),
				amount: String(item.amount || 0),
		  }))
		: [];

	const partsLineItemsArray = values.partsLineItems || [];
	const partsLineItems = hasLineItems(partsLineItemsArray)
		? partsLineItemsArray.map((item: any, index: number) => ({
				description: item.description || '',
				partId: item.partId || index + 1,
				partNumber: item.partNumber || '',
				unitType: item.unitType || '',
				quantity: stringifyLineItemValue(item.quantity, ''),
				unitPrice: stringifyLineItemValue(item.unitPrice, ''),
				amount: String(item.amount || 0),
		  }))
		: [];

	const equipmentLineItemsArray = values.equipmentLineItems || [];
	const equipmentLineItems = hasLineItems(equipmentLineItemsArray)
		? equipmentLineItemsArray.map((item: any, index: number) => ({
				description: item.description || '',
				equipmentId: item.equipmentId || index + 1,
				equipmentNumber: item.equipmentNumber || '',
				quantity: '1',
				unitType: 'each',
				unitPrice: stringifyLineItemValue(item.unitPrice, undefined),
				amount: String(item.amount || 0),
		  }))
		: [];

	const materialLineItems = [...partsLineItems, ...equipmentLineItems];

	const travelLineItemsArray = values.travelLineItems || [];
	const travelLineItems = hasLineItems(travelLineItemsArray)
		? travelLineItemsArray.map((item: any) => ({
				description: item.description || '',
				amount: String(item.amount || 0),
				...(item.id && { id: item.id }),
				...(item.unitPrice && { unitPrice: String(item.unitPrice) }),
		  }))
		: [];

	const freightLineItemsArray = values.freightLineItems || [];
	const freightLineItems = hasLineItems(freightLineItemsArray)
		? freightLineItemsArray.map((item: any) => ({
				description: item.description || '',
				amount: String(item.amount || 0),
				...(item.id && { id: item.id }),
				...(item.unitPrice && { unitPrice: String(item.unitPrice) }),
		  }))
		: [];

	const miscLineItemsArray = values.miscLineItems || [];
	const miscLineItems = hasLineItems(miscLineItemsArray)
		? miscLineItemsArray.map((item: any) => ({
				description: item.description || '',
				amount: String(item.amount || 0),
				...(item.id && { id: item.id }),
				...(item.unitPrice && { unitPrice: String(item.unitPrice) }),
		  }))
		: [];

	const taxKeys = values.taxKeys || [];
	const taxLineItems =
		taxKeys.length > 0
			? taxKeys.map((taxKey: number) => ({
					taxType: String(values[`tax-${taxKey}-taxType`] || ''),
					amount: getFixedFloatStringOrZero(Number(values[`tax-${taxKey}-amount`]) || 0),
			  }))
			: [];

	const dateOfInvoice = values.dateOfInvoice;
	const dueDate = values.dueDate;

	const laborTotal = Number(values.laborTotal) || 0;
	const partsTotal = Number(values.partsTotal) || 0;
	const equipmentTotal = Number(values.equipmentTotal) || 0;
	const travelTotal = Number(values.travelTotal) || 0;
	const freightTotal = Number(values.freightTotal) || 0;
	const miscTotal = Number(values.miscTotal) || 0;

	const laborTotalBeforeTax = getFixedFloatStringOrZero(laborTotal);
	const materialTotalBeforeTax = getFixedFloatStringOrZero(partsTotal + equipmentTotal);
	const travelTotalBeforeTax = getFixedFloatStringOrZero(travelTotal);
	const freightTotalBeforeTax = getFixedFloatStringOrZero(freightTotal);
	const miscTotalBeforeTax = getFixedFloatStringOrZero(miscTotal);

	const invoiceTotalBeforeTax = getFixedFloatStringOrZero(
		Number(values.invoiceTotalBeforeTax) || 0
	);
	const invoiceTax = getFixedFloatStringOrZero(Number(values.invoiceTax) || 0);
	const invoiceTotalAfterTax = getFixedFloatStringOrZero(Number(values.invoiceTotalAfterTax) || 0);

	const payload: any = {
		id: values.id,
		scope: scope !== null && scope !== undefined ? scope : '',
		// A project invoice has no work order to identify it in a list, so it carries its
		// own title instead of a work order number.
		...(isProjectInvoice
			? { title: values.title || '' }
			: {
					externalWorkOrderId: values.externalWorkOrderId
						? `${values.externalWorkOrderId}`
						: `${workOrderId}`,
			  }),
		invoiceNumber: values.invoiceNumber || '',
		paymentTerms: values.paymentTerms || '',
		dueDate,
		dateOfInvoice,
		invoiceTaxRate: values.invoiceTaxRate || null,
		invoiceTax,
		invoiceTotalBeforeTax,
		invoiceTotalAfterTax,
		laborTotalBeforeTax,
		laborLineItems,
		...(taxLineItems.length > 0 && { taxLineItems }),
		materialTotalBeforeTax,
		materialLineItems,
		freightTotalBeforeTax,
		freightLineItems,
		travelTotalBeforeTax,
		travelLineItems,
		miscTotalBeforeTax,
		miscLineItems,
		// Exactly one anchor key, never both and never a null placeholder.
		...(isProjectInvoice
			? {
					projectId: parseInt(String(projectId), 10),
					buyerCompanyId: parseInt(String(buyerCompanyId), 10),
					// Optional on a project invoice. On a create, omit the key rather than sending
					// NaN.
					//
					// KNOWN LIMITATION on update: the explicit null below does not actually reach
					// the API. FetchUtils.sanitize (fetch_utils.ts:113-126) strips every top-level
					// null from the PUT body, so clearing a location silently leaves the old one
					// in place, and the invoice stays in that location's spend reporting. Sent
					// anyway so it works the day the transport allows it.
					...(locationId !== undefined && locationId !== null
						? { locationId: parseInt(String(locationId), 10) }
						: isUpdate
						? { locationId: null }
						: {}),
			  }
			: { workOrderId: parseInt(String(workOrderId), 10) }),
		createdBy: currentUser.email,
		attachments: values.attachments || [],
		status: values.status || status || 'pending',
	};

	if (role === 'buyer') {
		if (baseMeta && baseMeta.buyerFacilityId !== undefined) {
			payload.buyerFacilityId = baseMeta.buyerFacilityId;
		}
		if (baseMeta && baseMeta.supplierFacilityId !== undefined) {
			payload.supplierFacilityId = baseMeta.supplierFacilityId;
		}
	}

	if (role === 'supplier') {
		if (baseMeta && baseMeta.supplierFacilityId !== undefined) {
			payload.supplierFacilityId = baseMeta.supplierFacilityId;
		}
		if (baseMeta && baseMeta.disputeNotes !== undefined) {
			payload.disputeNotes = baseMeta.disputeNotes;
		}
		// buyerFacilityId normally rides in only on the buyer branch because the API derives
		// it from the work order. A project invoice has no work order, so the API requires it
		// from the request whichever role is posting.
		if (isProjectInvoice && baseMeta && baseMeta.buyerFacilityId !== undefined) {
			payload.buyerFacilityId = baseMeta.buyerFacilityId;
		}
	}

	if (baseMeta && baseMeta.invoicePDFLink !== undefined) {
		payload.invoicePDFLink = baseMeta.invoicePDFLink;
	}

	if (baseMeta && baseMeta.isDeleted) {
		payload.isDeleted = true;
	}

	return payload;
};

export const getInvoiceDueDateBasedOnPaymentTerms = (getFieldValue, pt) => {
	const dateOfInvoice = getFieldValue('dateOfInvoice');

	if (!dateOfInvoice) return null;

	switch (pt) {
		case DEFAULT_PAYMENT_TERMS.NET_30:
			return dateOfInvoice.clone().add(30, 'days');
		case DEFAULT_PAYMENT_TERMS.NET_45:
			return dateOfInvoice.clone().add(45, 'days');
		case DEFAULT_PAYMENT_TERMS.NET_60:
			return dateOfInvoice.clone().add(60, 'days');
		case DEFAULT_PAYMENT_TERMS.NET_90:
			return dateOfInvoice.clone().add(90, 'days');
	}
};

const addSimpleLineItemsToFormData = (formData, fieldName) => {
	if (formData[`${fieldName}LineItems`] && formData[`${fieldName}LineItems`].length > 0) {
		const lineItems = formData[`${fieldName}LineItems`];
		formData[`${fieldName}Keys`] = lineItems.map((el, idx) => idx);
		lineItems.forEach((lineItem, idx) => {
			formData[`${fieldName}-${idx}-description`] = lineItem.description;
			formData[`${fieldName}-${idx}-unitPrice`] = lineItem.unitPrice;
			formData[`${fieldName}-${idx}-amount`] = lineItem.amount || lineItem.unitPrice || 0;
		});
		if (formData[`${fieldName}Keys`]) {
			formData[`${fieldName}TotalBeforeTax`] = formData[`${fieldName}Keys`]
				.reduce((acc, k2) => {
					const price = formData[`${fieldName}-${k2}-amount`] || 0;
					return getFixedFloatSum(acc, price);
				}, 0)
				.toString();
		}
	}
};

export const initialiseFormDataWithLineItems = (originalFormData, timeIn) => {
	const formData = JSON.parse(JSON.stringify(originalFormData));

	if (formData.laborLineItems) {
		formData.laborKeys = formData.laborLineItems.map((el, idx) => idx);
		formData.laborLineItems.forEach((lineItem, idx) => {
			formData[`labor-${idx}-roleType`] = lineItem.roleType;
			formData[`labor-${idx}-numberOfTech`] = lineItem.numberOfTech;
			formData[`labor-${idx}-time`] = parseFloat(
				`${lineItem.seconds / (timeIn === TIME_IN.MINUTES ? 60.0 : 3600.0)}`
			).toFixed(2);
			formData[`labor-${idx}-hourlyRate`] = lineItem.hourlyRate;
			formData[`labor-${idx}-amount`] =
				lineItem.amount || (lineItem.numberOfTech && lineItem.seconds && lineItem.hourlyRate)
					? (((lineItem.hourlyRate * lineItem.seconds) / 3600.0) * lineItem.numberOfTech).toString()
					: '0';
		});
		if (!formData[`laborTotalBeforeTax`]) {
			formData[`laborTotalBeforeTax`] = formData.laborKeys
				.reduce((acc, k) => {
					const price = formData[`labor-${k}-amount`] || 0;
					return getFixedFloatSum(acc, price);
				}, 0)
				.toString();
		}
	}

	const equipmentLineItems = nullSafeGetOrElse('materialLineItems', formData, []).filter(
		({ equipmentId }) => !!equipmentId
	);
	const partLineItems = nullSafeGetOrElse('materialLineItems', formData, []).filter(
		({ equipmentId }) => !equipmentId
	);

	if (nullSafeGetOrElse('materialLineItems', formData, []).length > 0) {
		formData.equipmentKeys = equipmentLineItems.map((_, idx) => idx);
		equipmentLineItems.forEach((lineItem, idx) => {
			formData[`equipment-${idx}-equipmentId`] = lineItem.equipmentId;
			formData[`equipment-${idx}-description`] = lineItem.description;
			formData[`equipment-${idx}-equipmentNumber`] = lineItem.equipmentNumber;
			formData[`equipment-${idx}-unitPrice`] = lineItem.unitPrice;
			formData[`equipment-${idx}-amount`] = lineItem.amount || lineItem.unitPrice || 0;
		});
		const equipmentTotal = formData.equipmentKeys.reduce(
			(acc, k) => getFixedFloatSum(acc, formData[`equipment-${k}-amount`] || 0),
			0
		);

		formData.partsKeys = partLineItems.map((_, idx) => idx);
		partLineItems.forEach((lineItem, idx) => {
			formData[`parts-${idx}-partId`] = lineItem.partId;
			formData[`parts-${idx}-description`] = lineItem.description;
			formData[`parts-${idx}-partNumber`] = lineItem.partNumber;
			formData[`parts-${idx}-unitType`] = lineItem.unitType;
			formData[`parts-${idx}-quantity`] = lineItem.quantity;
			formData[`parts-${idx}-unitPrice`] = lineItem.unitPrice;
			formData[`parts-${idx}-amount`] =
				lineItem.amount || lineItem.unitPrice * lineItem.quantity || 0;
		});
		const partsTotal = formData.partsKeys.reduce(
			(acc, k) => getFixedFloatSum(acc, formData[`parts-${k}-amount`] || 0),
			0
		);

		const materialTotal = parseFloat(formData.materialTotalBeforeTax) || 0;

		const { equipmentTotalBeforeTax, partsTotalBeforeTax } = getPartEquipmentTotalCostValues(
			equipmentLineItems,
			partLineItems,
			equipmentTotal,
			partsTotal,
			materialTotal
		);

		formData.equipmentTotalBeforeTax = equipmentTotalBeforeTax || 0;
		formData.partsTotalBeforeTax = partsTotalBeforeTax || 0;
	} else if (nullSafeGet('materialTotalBeforeTax', formData)) {
		formData[`partsTotalBeforeTax`] = formData.materialTotalBeforeTax;
	}

	addSimpleLineItemsToFormData(formData, LINE_ITEM_FIELD.MISC);
	addSimpleLineItemsToFormData(formData, LINE_ITEM_FIELD.TRAVEL);
	addSimpleLineItemsToFormData(formData, LINE_ITEM_FIELD.FREIGHT);
	addSimpleLineItemsToFormData(formData, LINE_ITEM_FIELD.INCURRED);

	if (formData.taxLineItems) {
		const taxLineItems = formData.taxLineItems;
		formData.taxKeys = taxLineItems.map((el, idx) => idx);
		taxLineItems.forEach((lineItem, idx) => {
			formData[`tax-${idx}-taxType`] = lineItem.taxType;
			formData[`tax-${idx}-amount`] = lineItem.amount;
		});
		if (!formData[`invoiceTax`]) {
			formData[`invoiceTax`] = formData.taxKeys
				.reduce((acc, k2) => {
					const price = formData[`tax-${k2}-amount`] || 0;
					return getFixedFloatSum(acc, price);
				}, 0)
				.toString();
		}
	}

	// Calculate invoiceTotalBeforeTax and invoiceTotalAfterTax if not already set
	// This ensures totals are calculated on initial load, not just when line items change
	if (!formData[`invoiceTotalBeforeTax`]) {
		const invoiceTotalBeforeTax = (
			parseFloat(nullSafeGetOrElse('laborTotalBeforeTax', formData, 0)) +
			parseFloat(nullSafeGetOrElse('partsTotalBeforeTax', formData, 0)) +
			parseFloat(nullSafeGetOrElse('equipmentTotalBeforeTax', formData, 0)) +
			parseFloat(nullSafeGetOrElse('freightTotalBeforeTax', formData, 0)) +
			parseFloat(nullSafeGetOrElse('travelTotalBeforeTax', formData, 0)) +
			parseFloat(nullSafeGetOrElse('miscTotalBeforeTax', formData, 0))
		).toFixed(2);
		formData[`invoiceTotalBeforeTax`] = invoiceTotalBeforeTax;
	}

	if (!formData[`invoiceTotalAfterTax`]) {
		const invoiceTotalAfterTax = (
			parseFloat(formData[`invoiceTotalBeforeTax`] || 0) +
			parseFloat(nullSafeGetOrElse('invoiceTax', formData, 0))
		).toFixed(2);
		formData[`invoiceTotalAfterTax`] = invoiceTotalAfterTax;
	}

	return {
		formData,
		labor: nullSafeGetOrElse('laborLineItems', formData, []).length,
		part: partLineItems.length,
		equipment: equipmentLineItems.length,
		misc: nullSafeGetOrElse(`${LINE_ITEM_FIELD.MISC}LineItems`, formData, []).length,
		travel: nullSafeGetOrElse(`${LINE_ITEM_FIELD.TRAVEL}LineItems`, formData, []).length,
		freight: nullSafeGetOrElse(`${LINE_ITEM_FIELD.FREIGHT}LineItems`, formData, []).length,
		incurred: nullSafeGetOrElse(`${LINE_ITEM_FIELD.INCURRED}LineItems`, formData, []).length,
		tax: nullSafeGetOrElse('taxLineItems', formData, []).length,
	};
};

const handleFirstAddSimpleLineItems = (state, setState, total, key, fieldName) =>
	setState(
		total
			? {
					[`${fieldName}ItemInitialValues`]: {
						...state[`${fieldName}ItemInitialValues`],
						[`${fieldName}-${key}-description`]: capitalize(
							LINE_ITEM_FIELD_TO_DISPLAY_NAMES[fieldName]
						),
						[`${fieldName}-${key}-unitPrice`]: total,
						[`${fieldName}-${key}-amount`]: total,
					},
			  }
			: {}
	);

export const handleFirstAddItem = (form, state, setState) => (keys, totalKeyName) => {
	if (keys.length === 1) {
		const total = form.getFieldValue(totalKeyName);
		const key = keys[0];
		switch (totalKeyName) {
			case 'laborTotalBeforeTax':
				setState(
					total
						? {
								laborItemInitialValues: {
									[`labor-${key}-numberOfTech`]: `${1}`,
									[`labor-${key}-time`]: `${1}`,
									[`labor-${key}-hourlyRate`]: `${total}`,
									[`labor-${key}-amount`]: `${total}`,
								},
						  }
						: {}
				);
				break;
			case 'partsTotalBeforeTax':
				setState(
					total
						? {
								partItemInitialValues: {
									[`parts-${key}-description`]: 'Part',
									[`parts-${key}-partNumber`]: '1',
									[`parts-${key}-quantity`]: 1,
									[`parts-${key}-unitPrice`]: total,
									[`parts-${key}-amount`]: total,
								},
						  }
						: {}
				);
				break;
			case 'equipmentTotalBeforeTax':
				setState(
					total
						? {
								equipmentItemInitialValues: {
									[`equipment-${key}-description`]: 'Equipment',
									[`equipment-${key}-equipmentNumber`]: '1',
									[`equipment-${key}-unitPrice`]: total,
									[`equipment-${key}-amount`]: total,
								},
						  }
						: {}
				);
				break;
			case `${LINE_ITEM_FIELD.MISC}TotalBeforeTax`:
				handleFirstAddSimpleLineItems(state, setState, total, key, LINE_ITEM_FIELD.MISC);
				break;
			case `${LINE_ITEM_FIELD.TRAVEL}TotalBeforeTax`:
				handleFirstAddSimpleLineItems(state, setState, total, key, LINE_ITEM_FIELD.TRAVEL);
				break;
			case `${LINE_ITEM_FIELD.FREIGHT}TotalBeforeTax`:
				handleFirstAddSimpleLineItems(state, setState, total, key, LINE_ITEM_FIELD.FREIGHT);
				break;
			case `${LINE_ITEM_FIELD.INCURRED}TotalBeforeTax`:
				handleFirstAddSimpleLineItems(state, setState, total, key, LINE_ITEM_FIELD.INCURRED);
				break;
		}
	}
};

export const addItem =
	(form, state, setState, existingKeys = undefined) =>
	(itemTypeVarName, uuid, totalKeyName) => {
		// can use data-binding to get
		const { getFieldValue, setFieldsValue } = form;
		const keys = getFieldValue(itemTypeVarName) || [];
		const nextKeys = keys.concat(uuid);
		// can use data-binding to set
		// important! notify form to detect changes
		setFieldsValue({
			[itemTypeVarName]: nextKeys,
		});
		handleFirstAddItem(form, state, setState)(nextKeys, totalKeyName);
		return uuid + 1;
	};

export const removeItem = (form, recalculateTotals) => (itemTypeCollectionKey, itemTypeKey, k) => {
	// can use data-binding to get
	const keys = form.getFieldValue(itemTypeCollectionKey);

	// can use data-binding to set
	form.setFieldsValue(
		{
			[itemTypeCollectionKey]: keys.filter((key) => key !== k),
		},
		() => {
			const itemKeys = form.getFieldValue(itemTypeCollectionKey);
			const itemTotalBeforeTax = itemKeys.reduce((acc, k2) => {
				const price = form.getFieldValue(`${itemTypeKey}-${k2}-amount`);
				return getFixedFloatSum(acc, price);
			}, 0);

			form.setFieldsValue(
				{ [`${itemTypeKey}TotalBeforeTax`]: itemTotalBeforeTax },
				recalculateTotals
			);
		}
	);
};

const getPartEquipmentTotalCostValues = (
	equipmentLineItems,
	partLineItems,
	equipmentTotal,
	partsTotal,
	materialTotal
) => {
	if (equipmentLineItems.length > 0 && partLineItems.length > 0) {
		return {
			equipmentTotalBeforeTax: equipmentTotal.toString(),
			partsTotalBeforeTax: partsTotal.toString(),
		};
	} else if (equipmentLineItems.length > 0) {
		return {
			equipmentTotalBeforeTax: equipmentTotal.toString(),
			partsTotalBeforeTax: getFixedFloatDifference(materialTotal, equipmentTotal).toString(),
		};
	} else if (partLineItems.length > 0) {
		return {
			equipmentTotalBeforeTax: getFixedFloatDifference(materialTotal, partsTotal).toString(),
			partsTotalBeforeTax: partsTotal.toString(),
		};
	} else return {};
};

const addSimpleLineItemsToInvoiceFromProposal =
	(form, state, setState, setFieldsValue) => (proposal, fieldName, uuid, copyFromFieldName?) => {
		let tempUuid = uuid;
		form.setFieldsValue({ [`${fieldName}Keys`]: [] });
		const totalLineItems = [
			...nullSafeGetOrElse(`${fieldName}LineItems`, proposal, []),
			...(copyFromFieldName
				? nullSafeGetOrElse(`${copyFromFieldName}LineItems`, proposal, [])
				: []),
		];
		totalLineItems.forEach((lineItem) => {
			const currentUUid = tempUuid;
			tempUuid = addItem(form, state, setState)(
				`${fieldName}Keys`,
				tempUuid,
				`${fieldName}TotalBeforeTax`
			);
			setFieldsValue({ [`${fieldName}-${currentUUid}-description`]: lineItem.description });
			setFieldsValue({ [`${fieldName}-${currentUUid}-unitPrice`]: lineItem.amount });
			setFieldsValue({
				[`${fieldName}-${currentUUid}-amount`]: lineItem.amount || lineItem.unitPrice || 0,
			});
		});

		return tempUuid;
	};

export const transferToQuoteFromProposal = (form, state, setState) => (formData, proposal) => {
	const materialTotalBeforeTax = nullSafeGetOrElse('materialTotalBeforeTax', proposal, 0);
	const laborTotalBeforeTax = nullSafeGetOrElse('laborTotalBeforeTax', proposal, 0);
	const freightTotalBeforeTax = nullSafeGetOrElse('freightTotalBeforeTax', proposal, 0);
	const miscTotalBeforeTax = nullSafeGetOrElse('miscTotalBeforeTax', proposal, 0);
	const travelTotalBeforeTax = nullSafeGetOrElse('travelTotalBeforeTax', proposal, 0);
	const incurredCostTotalBeforeTax = nullSafeGetOrElse('incurredCostTotalBeforeTax', proposal, 0);
	const invoiceTax = nullSafeGetOrElse('tax', proposal, 0);
	const scope = nullSafeGetOrElse('scope', proposal, '');

	const { setFieldsValue, getFieldValue } = form;

	setFieldsValue({ [`scope`]: scope });

	let equipmentUuid = 1;
	let partUuid = 1;
	let laborUuid = 1;
	let miscUuid = 1;
	let travelUuid = 1;
	let freightUuid = 1;

	if (
		materialTotalBeforeTax !== 0 &&
		nullSafeGetOrElse('materialLineItems', proposal, []).length > 0
	) {
		const equipmentLineItems = nullSafeGetOrElse('materialLineItems', proposal, []).filter(
			({ partId, partNumber }) => !partId && !partNumber
		);

		equipmentLineItems.forEach((lineItem) => {
			const currentUUid = equipmentUuid;
			equipmentUuid = addItem(form, state, setState)(
				'equipmentKeys',
				equipmentUuid,
				'equipmentTotalBeforeTax'
			);

			setFieldsValue({ [`equipment-${currentUUid}-description`]: lineItem.description });
			setFieldsValue({
				[`equipment-${currentUUid}-equipmentNumber`]:
					lineItem.equipmentNumber || lineItem.equipmentId,
			});
			setFieldsValue({ [`equipment-${currentUUid}-unitPrice`]: lineItem.unitPrice });
			setFieldsValue({
				[`equipment-${currentUUid}-amount`]: lineItem.amount || lineItem.unitPrice || 0,
			});
		});

		const equipmentTotal = (getFieldValue('equipmentKeys') || []).reduce(
			(acc, k) => getFixedFloatSum(acc, getFieldValue(`equipment-${k}-amount`) || 0),
			0
		);

		const partLineItems = nullSafeGetOrElse('materialLineItems', proposal, []).filter(
			({ partId, partNumber }) => partId || partNumber
		);

		partLineItems.forEach((lineItem) => {
			const currentUUid = partUuid;
			partUuid = addItem(form, state, setState)('partsKeys', partUuid, 'partsTotalBeforeTax');

			setFieldsValue({ [`parts-${currentUUid}-description`]: lineItem.description });
			setFieldsValue({
				[`parts-${currentUUid}-partNumber`]:
					lineItem.partNumber ||
					(lineItem.partId && nullSafeGetOrElse('partId', lineItem, '').toString()),
			});
			setFieldsValue({ [`parts-${currentUUid}-unitType`]: lineItem.unitType });
			setFieldsValue({ [`parts-${currentUUid}-quantity`]: lineItem.quantity });
			setFieldsValue({ [`parts-${currentUUid}-unitPrice`]: lineItem.unitPrice });
			setFieldsValue({
				[`parts-${currentUUid}-amount`]:
					lineItem.amount || lineItem.unitPrice * lineItem.quantity || 0,
			});
		});
		const partsTotal = (getFieldValue('partsKeys') || []).reduce(
			(acc, k) => getFixedFloatSum(acc, getFieldValue(`parts-${k}-amount`) || 0),
			0
		);

		const materialTotal = parseFloat(proposal.materialTotalBeforeTax) || 0;

		const { equipmentTotalBeforeTax, partsTotalBeforeTax } = getPartEquipmentTotalCostValues(
			equipmentLineItems,
			partLineItems,
			equipmentTotal,
			partsTotal,
			materialTotal
		);

		setFieldsValue({
			...(equipmentTotalBeforeTax && { equipmentTotalBeforeTax }),
			...(partsTotalBeforeTax && { partsTotalBeforeTax }),
		});
	} else if (nullSafeGet('materialTotalBeforeTax', proposal)) {
		setFieldsValue({ partsTotalBeforeTax: proposal.materialTotalBeforeTax });
	}

	setFieldsValue({ [`laborKeys`]: [] });
	nullSafeGetOrElse('laborLineItems', proposal, []).forEach((lineItem) => {
		const currentUUid = laborUuid;
		laborUuid = addItem(form, state, setState)('laborKeys', laborUuid, 'laborTotalBeforeTax');
		setFieldsValue({ [`labor-${currentUUid}-roleType`]: lineItem.roleType });
		setFieldsValue({ [`labor-${currentUUid}-numberOfTech`]: lineItem.numberOfTech });
		setFieldsValue({ [`labor-${currentUUid}-hourlyRate`]: lineItem.hourlyRate });
		setFieldsValue({
			[`labor-${currentUUid}-time`]: parseFloat(
				`${lineItem.seconds / (state.timeIn === TIME_IN.MINUTES ? 60.0 : 3600.0)}`
			).toFixed(2),
		});
		setFieldsValue({
			[`labor-${currentUUid}-amount`]:
				lineItem.amount || (lineItem.numberOfTech && lineItem.seconds && lineItem.hourlyRate)
					? (((lineItem.hourlyRate * lineItem.seconds) / 3600.0) * lineItem.numberOfTech).toString()
					: '0',
		});
	});
	setFieldsValue({ laborTotalBeforeTax: laborTotalBeforeTax.toString() });

	miscUuid = addSimpleLineItemsToInvoiceFromProposal(form, state, setState, setFieldsValue)(
		proposal,
		LINE_ITEM_FIELD.MISC,
		miscUuid,
		LINE_ITEM_FIELD.INCURRED
	);

	if (
		!nullSafeGet(`${LINE_ITEM_FIELD.INCURRED}LineItems.0`, proposal) &&
		parseFloat(incurredCostTotalBeforeTax) !== 0
	) {
		const currentUUid = miscUuid;
		miscUuid = addItem(form, state, setState)('miscKeys', miscUuid, 'miscTotalBeforeTax');
		setFieldsValue({ [`misc-${currentUUid}-amount`]: incurredCostTotalBeforeTax.toString() });
		setFieldsValue({
			[`misc-${currentUUid}-unitPrice`]: incurredCostTotalBeforeTax.toString(),
		});
		setFieldsValue({ [`misc-${currentUUid}-description`]: 'Incurred Cost' });
	}

	setFieldsValue({
		miscTotalBeforeTax: (
			parseFloat(miscTotalBeforeTax) + parseFloat(incurredCostTotalBeforeTax)
		).toString(),
	});

	freightUuid = addSimpleLineItemsToInvoiceFromProposal(
		form,
		state,
		setState,
		setFieldsValue
	)(proposal, LINE_ITEM_FIELD.FREIGHT, freightUuid);
	form.setFieldsValue({ freightTotalBeforeTax: freightTotalBeforeTax.toString() });

	travelUuid = addSimpleLineItemsToInvoiceFromProposal(
		form,
		state,
		setState,
		setFieldsValue
	)(proposal, LINE_ITEM_FIELD.TRAVEL, travelUuid);
	form.setFieldsValue({ travelTotalBeforeTax: travelTotalBeforeTax.toString() });
	form.setFieldsValue({ invoiceTax: invoiceTax });

	return {
		part: partUuid,
		equipment: equipmentUuid,
		labor: laborUuid,
		misc: miscUuid,
		freight: freightUuid,
		travel: travelUuid,
	};
};

export const initializeLineItemKeys = (formData, getFieldDecorator) => {
	getFieldDecorator('partsKeys', { initialValue: formData.partsKeys || [] });
	getFieldDecorator('equipmentKeys', { initialValue: formData.equipmentKeys || [] });
	getFieldDecorator(`${LINE_ITEM_FIELD.MISC}Keys`, {
		initialValue: formData[`${LINE_ITEM_FIELD.MISC}Keys`] || [],
	});
	getFieldDecorator(`${LINE_ITEM_FIELD.TRAVEL}Keys`, {
		initialValue: formData[`${LINE_ITEM_FIELD.TRAVEL}Keys`] || [],
	});
	getFieldDecorator(`${LINE_ITEM_FIELD.FREIGHT}Keys`, {
		initialValue: formData[`${LINE_ITEM_FIELD.FREIGHT}Keys`] || [],
	});
	getFieldDecorator(`${LINE_ITEM_FIELD.INCURRED}Keys`, {
		initialValue: formData[`${LINE_ITEM_FIELD.INCURRED}Keys`] || [],
	});
	getFieldDecorator('laborKeys', { initialValue: formData.laborKeys || [] });
	getFieldDecorator('taxKeys', { initialValue: formData.taxKeys || [] });
};

export const initialiseQuoteInvoiceCommonTotals = (formData, getFieldDecorator) => {
	getFieldDecorator(`materialTotalBeforeTax`, {
		initialValue: nullSafeGet(`materialTotalBeforeTax`, formData),
	});
	getFieldDecorator(`partsTotalBeforeTax`, {
		initialValue: nullSafeGet(`partsTotalBeforeTax`, formData),
	});

	getFieldDecorator(`laborTotalBeforeTax`, {
		initialValue: nullSafeGet(`laborTotalBeforeTax`, formData),
	});
	getFieldDecorator(`equipmentTotalBeforeTax`, {
		initialValue: nullSafeGet(`equipmentTotalBeforeTax`, formData),
	});
};

export const initializeInvoiceTotalFields = (formData, getFieldDecorator) => {
	getFieldDecorator(`invoiceTotalAfterTax`, {
		initialValue: nullSafeGet(`invoiceTotalAfterTax`, formData),
	});
	getFieldDecorator(`invoiceTotalBeforeTax`, {
		initialValue: nullSafeGet(`invoiceTotalBeforeTax`, formData),
	});

	initialiseQuoteInvoiceCommonTotals(formData, getFieldDecorator);
};

export const mergeProposalIntoFormData = (formData, proposal) => {
	const incurredCostLineItems =
		nullSafeGetOrElse('incurredCostLineItems', proposal, formData.incurredCostLineItems) || [];
	const incurredCostTotalBeforeTax = nullSafeGetOrElse(
		'incurredCostTotalBeforeTax',
		proposal,
		formData.incurredCostTotalBeforeTax
	);
	const miscLineItems = nullSafeGetOrElse('miscLineItems', proposal, formData.miscLineItems) || [];
	const miscTotalBeforeTax =
		nullSafeGetOrElse('miscTotalBeforeTax', proposal, formData.miscTotalBeforeTax) || 0;
	const materialLineItems =
		nullSafeGetOrElse('materialLineItems', proposal, formData.materialLineItems) || [];
	const materialTotalBeforeTax = nullSafeGetOrElse(
		'materialTotalBeforeTax',
		proposal,
		formData.materialTotalBeforeTax
	);
	const tax = nullSafeGetOrElse('tax', proposal, formData.tax);

	let finalMiscLineItems = [...miscLineItems, ...incurredCostLineItems];
	let finalMiscTotalBeforeTax = parseFloat(miscTotalBeforeTax) || 0;

	// Handle special case: if there's miscTotalBeforeTax but no miscLineItems,
	// create a misc item for it (similar to incurred costs)
	if (miscLineItems.length === 0 && parseFloat(miscTotalBeforeTax) !== 0) {
		finalMiscLineItems = [
			...finalMiscLineItems,
			{
				description: 'Miscellaneous',
				amount: miscTotalBeforeTax.toString(),
				unitPrice: miscTotalBeforeTax.toString(),
			},
		];
	}

	// Handle special case: if there's incurredCostTotalBeforeTax but no incurredCostLineItems/
	if (incurredCostLineItems.length === 0 && parseFloat(incurredCostTotalBeforeTax) !== 0) {
		finalMiscLineItems = [
			...finalMiscLineItems,
			{
				description: 'Incurred Cost',
				amount: incurredCostTotalBeforeTax.toString(),
				unitPrice: incurredCostTotalBeforeTax.toString(),
			},
		];
	}

	// Add incurredCostTotalBeforeTax to miscTotalBeforeTax
	if (parseFloat(incurredCostTotalBeforeTax) !== 0) {
		finalMiscTotalBeforeTax =
			parseFloat(miscTotalBeforeTax) + parseFloat(incurredCostTotalBeforeTax);
	}

	let partsTotalBeforeTax = nullSafeGetOrElse('partsTotalBeforeTax', formData, null);
	if (
		(!materialLineItems || materialLineItems.length === 0) &&
		materialTotalBeforeTax &&
		parseFloat(materialTotalBeforeTax) !== 0
	) {
		partsTotalBeforeTax = materialTotalBeforeTax;
	}

	return {
		...formData,
		scope: nullSafeGetOrElse('scope', proposal, formData.scope),
		laborLineItems: nullSafeGetOrElse('laborLineItems', proposal, formData.laborLineItems),
		materialLineItems: materialLineItems,
		miscLineItems: finalMiscLineItems,
		freightLineItems: nullSafeGetOrElse('freightLineItems', proposal, formData.freightLineItems),
		travelLineItems: nullSafeGetOrElse('travelLineItems', proposal, formData.travelLineItems),
		incurredCostLineItems: incurredCostLineItems,
		laborTotalBeforeTax: nullSafeGetOrElse(
			'laborTotalBeforeTax',
			proposal,
			formData.laborTotalBeforeTax
		),
		materialTotalBeforeTax: materialTotalBeforeTax,
		partsTotalBeforeTax:
			partsTotalBeforeTax !== null
				? partsTotalBeforeTax
				: nullSafeGetOrElse('partsTotalBeforeTax', formData, null),
		miscTotalBeforeTax: finalMiscTotalBeforeTax.toString(),
		freightTotalBeforeTax: nullSafeGetOrElse(
			'freightTotalBeforeTax',
			proposal,
			formData.freightTotalBeforeTax
		),
		travelTotalBeforeTax: nullSafeGetOrElse(
			'travelTotalBeforeTax',
			proposal,
			formData.travelTotalBeforeTax
		),
		incurredCostTotalBeforeTax: incurredCostTotalBeforeTax,
		tax: tax,
		invoiceTax: tax,
	};
};

// Utility function to compare parsed data with final submitted data
export const compareParsedWithFinal = (parsedSnapshot, finalInvoice) => {
	if (!parsedSnapshot) {
		return null;
	}

	const changedFields = [];
	let totalFieldsChanged = 0;

	// Compare top-level fields (normalize values for comparison)
	const normalizeValue = (val) => {
		if (val === null || val === undefined) return '';
		if (val instanceof Date) return val.toISOString();
		const num = parseFloat(String(val));
		if (!isNaN(num)) return num.toFixed(2);
		return String(val).trim();
	};

	const fieldsToCompare = ['invoiceNumber', 'invoiceTax', 'invoiceTotalAfterTax', 'paymentTerms'];

	fieldsToCompare.forEach((field) => {
		const parsedVal = normalizeValue(parsedSnapshot[field]);
		const finalVal = normalizeValue(finalInvoice[field]);
		if (parsedVal !== finalVal) {
			changedFields.push(field);
			totalFieldsChanged++;
		}
	});

	// Compare line items by category total (catches amount edits, not just count changes)
	const laborChanged =
		normalizeValue(parsedSnapshot.laborTotalBeforeTax) !==
		normalizeValue(finalInvoice.laborTotalBeforeTax)
			? 1
			: 0;

	const materialChanged =
		normalizeValue(parsedSnapshot.materialTotalBeforeTax) !==
		normalizeValue(finalInvoice.materialTotalBeforeTax)
			? 1
			: 0;

	const travelChanged =
		normalizeValue(parsedSnapshot.travelTotalBeforeTax) !==
		normalizeValue(finalInvoice.travelTotalBeforeTax)
			? 1
			: 0;

	const freightChanged =
		normalizeValue(parsedSnapshot.freightTotalBeforeTax) !==
		normalizeValue(finalInvoice.freightTotalBeforeTax)
			? 1
			: 0;

	const miscChanged =
		normalizeValue(parsedSnapshot.miscTotalBeforeTax) !==
		normalizeValue(finalInvoice.miscTotalBeforeTax)
			? 1
			: 0;

	const totalLineItemsChanged =
		laborChanged + materialChanged + travelChanged + freightChanged + miscChanged;

	return {
		total_fields_changed: totalFieldsChanged,
		changed_fields: changedFields,
		labor_line_items_changed: laborChanged,
		material_line_items_changed: materialChanged,
		travel_line_items_changed: travelChanged,
		freight_line_items_changed: freightChanged,
		misc_line_items_changed: miscChanged,
		total_line_items_changed: totalLineItemsChanged,
	};
};

// Returns the parsed-vs-system currency pair when they disagree, or null if
// they match (or if the parsed currency is missing). Caller is responsible for
// the page-specific Modal / navigation.
export const getCurrencyMismatch = (
	parsedData: any,
	currency: any
): { parsedCurrency: string; systemCurrency: string } | null => {
	const parsedCurrency = nullSafeGet('currency', parsedData) || 'USD';
	const systemCurrency = ((currency && currency.id) || 'USD').toUpperCase();
	if (!parsedCurrency || parsedCurrency.toUpperCase() === systemCurrency) {
		return null;
	}
	return { parsedCurrency, systemCurrency };
};

// ---------------------------------------------------------------------------------------------
// Invoice -> work order check.
//
// The extraction endpoint runs a calibrated "is this invoice for the work order it is being
// attached to" decision when the client passes `workOrderId` and the backend has the feature on
// (see openwrench-app docs/invoice-work-order-check-RUNBOOK.md). When it surfaces, the parsed
// payload carries a `work_order_check` alongside the ordinary extraction fields.
//
// Everything below is derivation only: the payload never says WHICH values disagreed, just how
// likely each named check passed. The invoice side of each comparison comes from the richer
// extraction fields the check switches on (service_location, referenced_work_order, ...), and the
// work order side from the record the form already holds.
// ---------------------------------------------------------------------------------------------

export const WORK_ORDER_CHECK_OUTCOMES = {
	MISMATCH: 'Mismatch',
	REVIEW: 'Review',
	CONSISTENT: 'Consistent',
};

export const WORK_ORDER_CHECK_QUESTIONS = {
	SERVICE_SITE_MATCHES: 'service_site_matches',
	REFERENCES_A_WO_OR_PO: 'references_a_wo_or_po',
	REFERENCED_NUMBER_MATCHES: 'referenced_number_matches',
	DATES_CONSISTENT: 'dates_consistent',
	SCOPE_MATCHES_TRADE: 'scope_matches_trade',
	SUPPLIER_MATCHES: 'supplier_matches',
};

// A noul at or above this is treated as passing, so its row is not worth showing. The backend
// routes the headline verdict off its own thresholds; this only decides which supporting rows
// are worth a person's attention once that verdict is already Mismatch.
const NOUL_FAILING_BELOW = 0.5;

/**
 * Which verdicts actually interrupt someone.
 *
 * Only `Mismatch` today. `Review` is built and deliberately switched off: it is the band where a
 * supplier trading as "Ecofit Pvt Ltd" against a record saying "Ecofit" would be flagged, and a
 * warning that is usually a false positive teaches people to dismiss the real ones. Add
 * WORK_ORDER_CHECK_OUTCOMES.REVIEW here to turn it on — the modal already renders it.
 *
 * `Consistent` never surfaces: if the invoice belongs here, a wording difference is not worth
 * stopping anyone for.
 */
const SURFACED_OUTCOMES = [WORK_ORDER_CHECK_OUTCOMES.MISMATCH];

export interface WorkOrderCheckComparisonRow {
	key: string;
	label: string;
	invoiceValue: string;
	workOrderValue: string;
}

export const getWorkOrderCheck = (parsedData: any) =>
	nullSafeGet('work_order_check', parsedData) || null;

export const getWorkOrderCheckOutcome = (workOrderCheck: any) =>
	nullSafeGet('outcome', workOrderCheck) || null;

export const shouldSurfaceWorkOrderCheck = (workOrderCheck: any) =>
	SURFACED_OUTCOMES.indexOf(getWorkOrderCheckOutcome(workOrderCheck)) !== -1;

/**
 * What the invoice printed about the work order's linked asset, when the check found anything
 * worth offering. Independent of the mismatch warning rather than an alternative to it: backend
 * PR #2425 removed the `Consistent`-only gate, so one parse can produce both a mismatch warning
 * and a suggestion. Deliberate — a suggestion is only ever an offer, and every field behind it is
 * opt-in, so the person who overrode the mismatch can judge this the same way.
 *
 * Echoed back verbatim on invoice create as `assetSuggestion` and stored on the record — the
 * backend explicitly does not re-derive it, so it must be passed through unmodified.
 */
export const getAssetSuggestion = (parsedData: any) =>
	nullSafeGet('work_order_check.asset_suggestion', parsedData) || null;

/** Suggested asset fields on a saved invoice, for the detail page. */
export const getInvoiceAssetSuggestion = (invoice: any) =>
	nullSafeGet('assetSuggestion', invoice) || null;

/**
 * Whether the suggestion is to LINK its asset to the work order (then enrich it) rather than to
 * enrich the asset already linked. Suggestions stored before the backend sent the flag are
 * field-only, so absent reads as false.
 */
export const isAssetLinkSuggestion = (assetSuggestion: any) =>
	nullSafeGet('isLinkSuggestion', assetSuggestion) === true;

/**
 * Absent reads as active: suggestions stored before the flag existed were only ever of linked assets.
 * nullSafeGetWithZero, not nullSafeGet — the latter turns `false` into undefined, which reads as active.
 */
export const isSuggestedAssetActive = (assetSuggestion: any) =>
	nullSafeGetWithZero('assetIsActive', assetSuggestion) !== false;

// Scalar id first so a relink is seen even when the asset itself is not hydrated.
const getWorkOrderLinkedAssetId = (workOrder: any) =>
	nullSafeGet('assetId', workOrder) || nullSafeGet('asset.id', workOrder);

/**
 * Whether a link suggestion still has its link step to offer: the work order does not yet link the
 * suggested asset. Once it does — linked from the banner or by hand — what is left is the ordinary
 * field suggestion, so the stored suggestion needs no clearing for the link to stop being offered.
 *
 * An invoice that does not carry its work order answers true: the link is the offer, and linking an
 * asset that is already linked is a no-op PATCH, so showing it costs nothing.
 */
export const isAssetLinkPending = (invoice: any) => {
	const assetSuggestion = nullSafeGet('assetSuggestion', invoice);
	if (!isAssetLinkSuggestion(assetSuggestion)) {
		return false;
	}
	const workOrder = nullSafeGet('workOrder', invoice);
	if (!workOrder) {
		return true;
	}
	return `${getWorkOrderLinkedAssetId(workOrder)}` !== `${nullSafeGet('assetId', assetSuggestion)}`;
};

/**
 * Whether the stored suggestion should be offered at all.
 *
 * A link suggestion always is: it names an asset the work order did not link at parse time, so a
 * work order that links something else (or nothing) is exactly the case it exists for — replacing
 * the current link is part of the offer. A field suggestion is only offered while its asset is still
 * the linked one; see isSuggestedAssetStillLinked.
 *
 * Known limit: the stored suggestion never clears, so if someone links the suggested asset and later
 * relinks the work order elsewhere by hand, the link is offered again.
 */
export const isAssetSuggestionOfferable = (invoice: any) =>
	isAssetLinkSuggestion(nullSafeGet('assetSuggestion', invoice)) ||
	isSuggestedAssetStillLinked(invoice);

/**
 * Whether the asset a FIELD suggestion is about is still the one its work order links. Link
 * suggestions answer through isAssetLinkPending instead.
 *
 * A suggestion names the work order's linked asset as it was at parse time; that link can be
 * changed afterwards. Once it has been, the suggestion describes a unit this work order no longer
 * has, and offering to write a serial or model onto it would put invoice data on the wrong
 * equipment. So the offer is withdrawn entirely rather than retargeted.
 *
 * Reads the work order's scalar `assetId` first so a relink is caught even when the asset itself
 * is not hydrated.
 *
 * A work order that now names NO asset counts as changed, not as unknown: the backend only ever
 * produces a suggestion when the work order had a linked asset at parse time (its
 * `case (Some(asset), Seq(mention))`), so a suggestion sitting beside an assetless work order is
 * proof the link was removed since. The one genuinely unknowable case is an invoice that does not
 * carry its work order at all, and only that answers true.
 */
export const isSuggestedAssetStillLinked = (invoice: any) => {
	const suggestedAssetId = nullSafeGet('assetSuggestion.assetId', invoice);
	if (!suggestedAssetId) {
		return true;
	}
	const workOrder = nullSafeGet('workOrder', invoice);
	if (!workOrder) {
		// Nothing to compare against — do not hide a real suggestion over missing hydration.
		return true;
	}
	const linkedAssetId = getWorkOrderLinkedAssetId(workOrder);
	if (!linkedAssetId) {
		return false;
	}
	return `${linkedAssetId}` === `${suggestedAssetId}`;
};

/**
 * The asset the suggestion is about, when the invoice already carries it hydrated through its
 * work order — which saves a read on the common path.
 *
 * Guarded on the id matching, same reasoning as isSuggestedAssetStillLinked. Note that a null here
 * means only "nothing usable is hydrated"; it is not a relink signal, and callers must not treat
 * it as one — that is what isSuggestedAssetStillLinked is for.
 */
export const getHydratedSuggestedAsset = (invoice: any) => {
	const suggestedAssetId = nullSafeGet('assetSuggestion.assetId', invoice);
	const asset = nullSafeGet('workOrder.asset', invoice);
	if (!suggestedAssetId || !asset) {
		return null;
	}
	return `${nullSafeGet('id', asset)}` === `${suggestedAssetId}` ? asset : null;
};

export const getAssetSuggestionFields = (assetSuggestion: any) => {
	const fields = nullSafeGetOrElse('fields', assetSuggestion, []);
	return Array.isArray(fields) ? fields : [];
};

// The backend names the offerable fields `make`, `model` and `serial` (its SuggestableFields); the
// asset record spells them differently. Anything outside this map is not something we know how to
// write, so it is dropped rather than guessed at — which is what `tag` relies on, the one
// enrichable field the backend deliberately never offers.
export const ASSET_SUGGESTION_FIELD_TO_ASSET_FIELD = {
	make: 'manufacturer',
	model: 'modelNumber',
	serial: 'serialNumber',
};

export const ASSET_FIELD_LABELS = {
	make: 'Manufacturer',
	model: 'Model Number',
	serial: 'Serial Number',
};

/**
 * The suggested fields still worth showing, each paired with the asset field it would write.
 *
 * `currentValue` is re-read from the asset record rather than trusted from the suggestion: the
 * suggestion records what the record held when the invoice was parsed, which may be days old and
 * may already have been edited by hand.
 *
 * A field whose record value already equals what the invoice printed is dropped — there is nothing
 * to review. That is also what makes the banner go away after a successful enrich, without needing
 * the backend to clear the stored suggestion.
 */
export const getSuggestedAssetFieldUpdates = (assetSuggestion: any, asset: any) =>
	getAssetSuggestionFields(assetSuggestion)
		.map((suggestedField) => {
			const field = nullSafeGet('field', suggestedField);
			const assetField = ASSET_SUGGESTION_FIELD_TO_ASSET_FIELD[field];
			if (!assetField) {
				return null;
			}
			const invoiceValue = nullSafeGetOrElse('invoiceValue', suggestedField, '');
			if (`${invoiceValue}`.trim() === '') {
				return null;
			}
			// Before the asset loads there is nothing to compare against, so show every row and let
			// the comparison narrow it once the record arrives.
			const currentValue = asset
				? nullSafeGetOrElse(assetField, asset, '')
				: nullSafeGetOrElse('currentValue', suggestedField, '');
			if (`${currentValue}`.trim() === `${invoiceValue}`.trim()) {
				return null;
			}
			return {
				field,
				assetField,
				invoiceValue,
				currentValue,
				probability: nullSafeGet('probability', suggestedField),
			};
		})
		.filter((field) => field !== null);

const joinNonEmpty = (parts, separator = ', ') =>
	parts
		.filter((part) => part !== undefined && part !== null && `${part}`.trim() !== '')
		.join(separator);

// service_location as the extractor reports it: a verbatim transcription when it could read one,
// otherwise the pieces it managed to split out.
const getExtractedServiceLocation = (parsedData) => {
	const serviceLocation = nullSafeGet('service_location', parsedData);
	if (!serviceLocation) {
		return '';
	}
	const rawText = nullSafeGetOrElse('raw_text', serviceLocation, '');
	if (rawText) {
		return rawText;
	}
	return joinNonEmpty([
		serviceLocation.site_name,
		serviceLocation.street_address,
		serviceLocation.city,
		serviceLocation.region,
		serviceLocation.postcode,
	]);
};

const getWorkOrderServiceLocation = (workOrder) => {
	const location = nullSafeGet('location', workOrder);
	const address = nullSafeGet('buyerFacility.primaryAddress', location);
	// Region and postcode join with a space ("GA 30309"), everything else with a comma — the
	// ordinary way an address is written, and what the invoice side will have been printed as.
	const addressText = address
		? joinNonEmpty([
				address.streetAddress1,
				address.city,
				joinNonEmpty([address.region, address.postcode], ' '),
		  ])
		: '';
	return joinNonEmpty([nullSafeGet('name', location), addressText], ' — ');
};

// A work order or PO number as printed on the invoice. The extractor reports both slots; either
// one is what the check compared against, so show whichever it found.
//
// `normalized` first: the extractor strips the printed label and punctuation there ("WO# 123-45"
// becomes "12345"), which is what makes this cell scannable against the bare work order number
// beside it. raw_text is the fallback when it could not normalize.
const getExtractedReferencedNumber = (parsedData) =>
	nullSafeGetOrElse('referenced_work_order.normalized', parsedData, '') ||
	nullSafeGetOrElse('referenced_work_order.raw_text', parsedData, '') ||
	nullSafeGetOrElse('referenced_po.normalized', parsedData, '') ||
	nullSafeGetOrElse('referenced_po.raw_text', parsedData, '');

// Bare, and one value only: this sits directly beside the number the invoice printed, and the two
// cells are meant to be compared at a glance. Labelling and concatenating them defeats that.
const getWorkOrderReferencedNumber = (workOrder) =>
	`${
		nullSafeGet('woNumber', workOrder) ||
		nullSafeGet('id', workOrder) ||
		nullSafeGetOrElse('poNumber', workOrder, '')
	}`;

const getExtractedServiceDates = (parsedData) => {
	const serviceDates = nullSafeGetOrElse('service_dates', parsedData, []);
	if (!Array.isArray(serviceDates)) {
		return '';
	}
	return joinNonEmpty(serviceDates.map((serviceDate) => nullSafeGet('raw_text', serviceDate)));
};

const getWorkOrderServiceDates = (workOrder) => {
	const completedAt = nullSafeGet('completedAt', workOrder);
	const dueDate = nullSafeGet('dueDate', workOrder);
	const formatted = completedAt
		? `Completed ${dateFormatter(completedAt, DATE_ONLY_FORMAT)}`
		: dueDate
		? `Due ${dateFormatter(dueDate, DATE_ONLY_FORMAT)}`
		: '';
	return formatted;
};

const getExtractedScope = (parsedData) =>
	joinNonEmpty(
		[
			nullSafeGet('scope_summary.summary', parsedData),
			nullSafeGet('scope_summary.implied_trade', parsedData),
		],
		' · '
	);

const getWorkOrderScope = (workOrder) =>
	nullSafeGet('problemType.hierarchyName', workOrder) ||
	nullSafeGet('problemType.name', workOrder) ||
	nullSafeGetOrElse('title', workOrder, '');

/**
 * One row per noul the backend can answer, in the order a reader should scan them.
 *
 * `references_a_wo_or_po` is deliberately absent: it asks whether the invoice prints a number at
 * all, which has no work-order side to compare against. An invoice with no printed number already
 * shows up as an empty invoice value on the referenced_number_matches row.
 */
const NOUL_ROW_MAP = [
	{
		key: WORK_ORDER_CHECK_QUESTIONS.REFERENCED_NUMBER_MATCHES,
		label: 'WO #',
		getInvoiceValue: getExtractedReferencedNumber,
		getWorkOrderValue: getWorkOrderReferencedNumber,
	},
	{
		key: WORK_ORDER_CHECK_QUESTIONS.SUPPLIER_MATCHES,
		label: 'Supplier',
		getInvoiceValue: (parsedData) => nullSafeGetOrElse('vendor_name', parsedData, ''),
		getWorkOrderValue: (workOrder) =>
			nullSafeGet('supplierFacility.displayName', workOrder) ||
			nullSafeGetOrElse('supplierFacility.name', workOrder, ''),
	},
	{
		key: WORK_ORDER_CHECK_QUESTIONS.SERVICE_SITE_MATCHES,
		label: 'Location',
		getInvoiceValue: getExtractedServiceLocation,
		getWorkOrderValue: getWorkOrderServiceLocation,
	},
	{
		key: WORK_ORDER_CHECK_QUESTIONS.DATES_CONSISTENT,
		label: 'Service date',
		getInvoiceValue: getExtractedServiceDates,
		getWorkOrderValue: getWorkOrderServiceDates,
	},
	{
		key: WORK_ORDER_CHECK_QUESTIONS.SCOPE_MATCHES_TRADE,
		label: 'Scope',
		getInvoiceValue: getExtractedScope,
		getWorkOrderValue: getWorkOrderScope,
	},
];

/**
 * The invoice-vs-work-order rows worth showing: the checks that actually came back failing, each
 * paired with the two values a person would compare by eye.
 *
 * Every comparison the backend answered is shown, to both roles. An earlier version narrowed the
 * set per role on the theory that some rows were noise; in practice a reader who is being told the
 * invoice looks wrong wants the whole basis for that, and a withheld row reads as the check having
 * missed something.
 *
 * A row is dropped when the noul passed, when the backend did not answer it, or when neither side
 * has a value — a comparison with nothing on either side tells the reader less than no row at all.
 */
export const buildWorkOrderCheckComparisonRows = (
	workOrderCheck: any,
	parsedData: any,
	workOrder: any
): WorkOrderCheckComparisonRow[] => {
	const nouls = nullSafeGetOrElse('validation.nouls', workOrderCheck, []);
	if (!Array.isArray(nouls) || nouls.length === 0) {
		return [];
	}
	const probabilityByQuestion = {};
	nouls.forEach((noul) => {
		const question = nullSafeGet('question', noul);
		if (question) {
			// WithZero: a probability of exactly 0 is the strongest possible failure for this check,
			// and plain nullSafeGet would read it as unanswered and drop the row.
			probabilityByQuestion[question] = nullSafeGetWithZero('probability', noul);
		}
	});

	return NOUL_ROW_MAP.map((row) => {
		const probability = probabilityByQuestion[row.key];
		if (typeof probability !== 'number' || probability >= NOUL_FAILING_BELOW) {
			return null;
		}
		const invoiceValue = row.getInvoiceValue(parsedData) || '';
		const workOrderValue = row.getWorkOrderValue(workOrder) || '';
		if (!invoiceValue && !workOrderValue) {
			return null;
		}
		return { key: row.key, label: row.label, invoiceValue, workOrderValue };
	}).filter((row) => row !== null);
};

interface ApplyParsedInvoiceToFormOptions {
	parsedData: any;
	workOrder: any;
	// Stands in for the work order when there isn't one (project invoices). Only needs a
	// `location`, which supplies the timezone used to read the dates printed on the PDF.
	anchor?: any;
	userType: string;
	setInvoiceFormData: (updater: (prev: any) => any) => void;
	setParsedFormDataSnapshot: (snapshot: any) => void;
	setParsedDiscount: (discount: any) => void;
	setDisableCosts: (disabled: boolean) => void;
}

/**
 * Merges AI-parsed invoice data into the V2 form state and emits the
 * `invoice_pdf_parsed` PostHog event with the right shape. Shared by
 * BuyerInvoiceAIForm and SupplierInvoiceAIForm — those pages used to
 * carry character-for-character duplicates of this block.
 *
 * Does NOT touch invoicePDFLink (that arrives separately via onS3UrlReady) or
 * the currency-mismatch flow (use getCurrencyMismatch upstream — copy/buttons
 * differ per role).
 */
export const applyParsedInvoiceToForm = (options: ApplyParsedInvoiceToFormOptions) => {
	const {
		parsedData,
		workOrder,
		anchor,
		userType,
		setInvoiceFormData,
		setParsedFormDataSnapshot,
		setParsedDiscount,
		setDisableCosts,
	} = options;

	// A project invoice has no work order; it passes `anchor` instead.
	if (!workOrder && !anchor) {
		posthog.capture('invoice_pdf_parsed', {
			user_type: userType,
			has_parsed_data: false,
			parsed_line_items_count: 0,
			parsed_fields: [],
			error: 'no_parsed_data',
		});
		return;
	}

	const adaptedFormData = adaptParsedInvoiceToFormData(parsedData, workOrder, anchor);
	if (!adaptedFormData) {
		posthog.capture('invoice_pdf_parsed', {
			user_type: userType,
			has_parsed_data: false,
			error: 'adaptation_failed',
		});
		return;
	}

	setInvoiceFormData((prevFormData) => ({
		...prevFormData,
		...adaptedFormData,
		id: prevFormData.id,
		status: prevFormData.status,
		supplierFacilityId: prevFormData.supplierFacilityId,
		buyerFacilityId: prevFormData.buyerFacilityId,
	}));
	setParsedFormDataSnapshot(adaptedFormData);

	const discount = nullSafeGet('discount', parsedData);
	setParsedDiscount(discount);
	setDisableCosts((discount || 0) > 0);

	const hasDiscount = (discount || 0) > 0;
	posthog.capture('invoice_pdf_parsed', {
		user_type: userType,
		has_parsed_data: true,
		has_discount: hasDiscount,
		...(hasDiscount && discount != null && { discount }),
	});
};
